import React, {useEffect, useRef, useState} from "react"
import {useNavigate} from "react-router-dom";
import {useAppDispatch, useAppSelector} from "../../../redux/reducer/store";
import {
    isActivityCarBusState,
    isActivityFlightState,
    isActivityHotelState,
    isActivityWithReturnRouteProps,
    isDefined,
} from "../../../utils/check/type-guards";
import {useTranslation} from "react-i18next";
import "./styles.css"
import {
    deleteActivityFromTravelComponents,
    deleteActivityFromTravelComponentsQueue,
    undoDeleteActivityFromTravelComponents
} from "../../../redux/reducer/travel-components";
import {toast, ToastItem, TypeOptions} from "react-toastify";
import {get, isEqual, orderBy} from "lodash"
import {
    DataGrid,
    DataGridDataRowProps,
    DataGridHeaderRowProps,
    DataGridRowActionInterface,
    DropdownOption,
    getSortProps,
    IconAndText,
    IconProps,
    SORT_ORDER,
    SortElementProps,
    Text,
    TextButton
} from "atmosfair-ui"
import {PATH_SCREEN_STRING, PATH_STRINGS} from "../../../routes/route-props";
import ToastWithButton from "../../../molecules/toasts/toast-with-button/ToastWithButton";
import {CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL} from "../../../api/model/common";
import {ACTIVITY_TYPE, AllActivityTypes, AllActivityTypesArray} from "../../../types-enums/activity";
import {roundCo2EPerPerson} from "../../../utils/math/round-values";
import cloneDeep from "lodash/cloneDeep";
import {theme} from "../../../styles/theme";
import {getFirstLeg, getLastLeg} from "../../../utils/getter/flight";
import FlightRoute from "../../../molecules/activity/string-display/flight-route/FlightRoute";
import HotelLocation from "../../../molecules/activity/string-display/hotel-location/HotelLocation";
import TopButtonsWrapper from "../../../components/button-groups/top-buttons-wrapper/TopButtonsWrapper";
import DownloadPdfButton from "../../buttons/download-pdf-button/DownloadPdfButton";
import LoadBundlesButton from "../../buttons/load-bundles-button/LoadBundlesButton";
import SaveBundlesButton from "../../buttons/save-bundles-button/SaveBundlesButton";
import CopyToClipboardButton from "../../buttons/copy-to-clipboard-button/CopyToClipboardButton";
import CreateJourneyFromTravelComponentsButton
    from "../../buttons/create-journey-from-travel-components-button/CreateJourneyFromTravelComponentsButton";
import {hasExactDates} from "../../../utils/getter/journey";
import {
    formatDateString,
    generateTranslatedActivityTypeString,
    NO_DATA_STRING,
    WhereToDisplayActivityString
} from "../../../utils/string/common";
import {getTotalCo2KgEEmissionsAndCalculationStatus} from "../../../utils/getter/emissions";
import {BundleType} from "../../buttons/common";
import DeleteBundlesButton from "../../buttons/delete-bundles-button/DeleteBundlesButton";
import ActivityEmissionDisplay
    from "../../../molecules/activity/string-display/activity-emission-display/ActivityEmissionDisplay";
import {getDurationInDaysOrNights} from "../../../utils/getter/common";
import CarBusInformation from "../../../molecules/activity/string-display/car-bus-information/CarBusInformation";
import {convertJourneyDurationToDropdownFormat} from "../../../utils/converter/dropdown";
import {createArrayFromNumber} from "../../../utils/common";
import useRecalculateActivityOnDifferentDate from "../../../hooks/use-recalculate-activity-on-different-date";
import ToastWithTextOnly from "../../../molecules/toasts/toast-with-text-only/ToastWithTextOnly";
import {recalculateActivityOnDifferentDate} from "../../../utils/check/activities";
import {addActivityToJourney} from "../../../redux/reducer/journey";
import {getHotelDurationInNightsWithOptionalOriginalHotelStay} from "../../../utils/getter/hotel";
import {getActivityPersonCount, getReturnAndOneWayActivity} from "../../../utils/getter/activities";
import {DateTime} from "luxon";

enum KEYPATH_TO_VALUE {
    calculatedEmissions = "calculatedEmissions.co2",
    iata = "legs.firstLeg.airport.iata",
    passengerCount = "passengerCount",
    city = "city.name",
    fuelType = "fuelType"
}

const TravelComponentsTable = () => {
    const {
        activities,
        bundleId,
    } = useAppSelector(state => state.travelComponents)
    const {journeyDuration} = useAppSelector(state => state.journey)
    const [bundleHasBeenLoaded, setBundleHasBeenLoaded] = useState(false)
    const [sortOrderName, setSortOrderName] = useState<SortElementProps["active"]>(null)
    const [sortOrderEmissions, setSortOrderEmissions] = useState<SortElementProps["active"]>(null)
    const [sortOrderTravelersCount, setSortOrderTravelersCount] = useState<SortElementProps["active"]>(null)
    const [headers, setHeaders] = useState<DataGridHeaderRowProps["headers"]>([])
    const [rows, setRows] = useState<DataGridDataRowProps["rows"]>([])
    const [selectedRows, setSelectedRows] = useState<number[]>([])
    const [groupRows, setGroupRows] = useState(true)
    const toastCount = useRef(0)
    const dayToAdd = useRef<number | null>(null)
    const navigate = useNavigate()
    const {i18n, t} = useTranslation();
    const {resolvedLanguage} = i18n;
    const dispatch = useAppDispatch()
    let journeyDurationInDays = getDurationInDaysOrNights(journeyDuration, false)
    const journeyHasExactDates = hasExactDates(journeyDuration)
    const {
        isLoading,
        updatedActivities,
        recalculateActivityEmissionWitNewGivenDay
    } = useRecalculateActivityOnDifferentDate({
        journeyDuration,
        renderToast: (message, type) => _renderToast(message, type),
    })

    useEffect(() => {
        setBundleHasBeenLoaded(bundleId !== null)
    }, [bundleId])

    useEffect(() => {
        setHeaders(_getHeaders())
    }, [sortOrderEmissions, sortOrderName, sortOrderTravelersCount]);

    useEffect(() => {
        setRows(_setDataGridRows())
    }, [activities, resolvedLanguage]);

    useEffect(() => {
        if (updatedActivities) _addActivityToJourney(updatedActivities[0])
    }, [updatedActivities])

    const _renderToast = (message: string, type: TypeOptions) => toast(<ToastWithTextOnly
            message={message}/>,
        {progress: undefined, type}
    )

    const _deleteActivity = (index: number, activity: AllActivityTypes) => {
        const {common: {id, activityType}} = activity
        dispatch(deleteActivityFromTravelComponents({id, onlyDeleteReturnActivity: false}))
        toastCount.current += 1
        _renderDeleteActivityToast(id, index, activityType)
    }

    const _getHeaders = () => [
        {
            text: "GENERAL.NAME",
            sort: {
                onCaretDown: () => {
                    setSortOrderName(SORT_ORDER.ASC)
                    setSortOrderEmissions(SORT_ORDER.ASC)
                    setSortOrderTravelersCount(null)
                },
                onCaretUp: () => {
                    setSortOrderName(SORT_ORDER.DESC)
                    setSortOrderEmissions(SORT_ORDER.DESC)
                    setSortOrderTravelersCount(null)
                },
                disabled: false,
                sortOrder: sortOrderName,
            },
        },
        {
            text: "GENERAL.TRAVELERS",
            sort: {
                onCaretDown: () => {
                    setSortOrderTravelersCount(SORT_ORDER.ASC)
                    setSortOrderEmissions(null)
                    setSortOrderName(null)
                },
                onCaretUp: () => {
                    setSortOrderTravelersCount(SORT_ORDER.DESC)
                    setSortOrderEmissions(null)
                    setSortOrderName(null)
                },
                disabled: false,
                sortOrder: sortOrderTravelersCount,
            }
        },
        {text: "GENERAL.DATE_OF_ACTIVITY"},
        {
            text: "GENERAL.EMISSIONS_PER_PERSON",
            sort: {
                onCaretDown: () => {
                    setSortOrderEmissions(SORT_ORDER.ASC)
                    setSortOrderTravelersCount(null)
                    setSortOrderName(null)
                },
                onCaretUp: () => {
                    setSortOrderEmissions(SORT_ORDER.DESC)
                    setSortOrderTravelersCount(null)
                    setSortOrderName(null)
                },
                sortOrder: sortOrderEmissions,
                disabled: false,
            },
        }
    ]

    const _renderDeleteActivityToast = (
        queueId: string,
        index: number,
        activityType: string,
    ) => {
        let undoClicked = false;
        const toastId = toast(
            <ToastWithButton
                message={t(
                    "TOASTS.SUCCESS.UNDO_DELETE_ACTIVITY_FROM_TRAVEL_COMPONENTS_TABLE",
                    {
                        activity: generateTranslatedActivityTypeString(activityType, i18n),
                        interpolation: {escapeValue: false}
                    }
                )}
                onClick={() => {
                    undoClicked = true
                    dispatch(undoDeleteActivityFromTravelComponents({
                        id: queueId,
                        index,
                    }))
                    if (toastCount.current > 0) toastCount.current -= 1
                    toast.dismiss(toastId)
                }}/>
            ,
            {
                progress: undefined,
                type: 'success',
            }
        );

        const unsubscribe = toast.onChange((payload: ToastItem) => {
            switch (payload.status) {
                case "added":
                    break;
                case "updated":
                    break;
                case "removed":
                    if (!undoClicked && payload.id === toastId) {
                        if (toastCount.current > 0) toastCount.current -= 1
                        dispatch(deleteActivityFromTravelComponentsQueue(queueId))
                    }
                    if (toastCount.current === 0) unsubscribe()
                    break;
            }
        })
    }

    const _setLastTableCellFromRow = (
        status: CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL | null,
        customCell: React.ReactNode | null,
        co2ETotal: number | null,
        activity: AllActivityTypes
    ) => {
        const personCount = getActivityPersonCount(activity)
        if (personCount === null) return {
            customCell: <Text>ERRORS.COMMON.GENERAL</Text>,
            value: 0,
            keyPathToValue: status === CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL.success_with_errors ? "calculatedEmissions.co2" : undefined,
        }

        if (status !== CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL.success) {
            return {
                customCell,
                value: status === CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL.success_with_errors ? roundCo2EPerPerson(co2ETotal!, personCount) : -1,
                keyPathToValue: status === CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL.success_with_errors ? "calculatedEmissions.co2" : undefined,
            }
        } else {
            return {
                customCell,
                value: roundCo2EPerPerson(co2ETotal!, personCount),
                keyPathToValue: KEYPATH_TO_VALUE.calculatedEmissions,
            }
        }
    }

    const _addTravelComponentsToJourney = (
        disabled: boolean,
        index: number,
        activity: AllActivityTypes
    ): DataGridRowActionInterface[] | undefined => {
        if (journeyDurationInDays !== null) {
            const {common: {activityType}} = activity
            let adjustedJourneyDurationInDays = journeyDurationInDays
            let conditionallyDisabled = false
            switch (activityType) {
                case ACTIVITY_TYPE.car_bus:
                    if (isActivityCarBusState(activity)) {
                        const {countDays} = activity
                        if (countDays > journeyDurationInDays) conditionallyDisabled = true
                        adjustedJourneyDurationInDays -= countDays
                    }
                    break;
                case ACTIVITY_TYPE.flight:
                    break;
                case ACTIVITY_TYPE.hotel:
                    if (isActivityHotelState(activity)) {
                        const hotelStayDurationInNights = getHotelDurationInNightsWithOptionalOriginalHotelStay(activity, false)
                        if (journeyDurationInDays <= 1) conditionallyDisabled = true
                        if (hotelStayDurationInNights !== null) {
                            adjustedJourneyDurationInDays -= hotelStayDurationInNights
                            if (hotelStayDurationInNights > journeyDurationInDays - 1) conditionallyDisabled = true
                        }
                    }
                    break;
                default:
                    activityType satisfies never;
            }

            return [
                {
                    disabled: disabled || isLoading || conditionallyDisabled,
                    setSelection: (selection: DropdownOption[] | null) => _onSelectAddActivityToJourney(index, selection),
                    dropdownLabel: t("LABELS.TRAVEL_COMPONENTS.ADD_TO_JOURNEY", {
                        activity: generateTranslatedActivityTypeString(activityType, i18n),
                        interpolation: {escapeValue: false}
                    }),
                    dropdownOptions: convertJourneyDurationToDropdownFormat(createArrayFromNumber(adjustedJourneyDurationInDays), i18n),
                    icon: "suitcase" as IconProps["icon"],
                    tooltip: "TOOLTIP.TRAVEL_COMPONENTS.ADD_TO_JOURNEY",
                    onClick: () => {
                    }
                }
            ]
        }
    }

    const _addActivity = (activity: AllActivityTypes) => {
        const updatedActivity = cloneDeep(activity)
        // transformActivityToJourneyState(updatedActivity)
        if (dayToAdd.current !== null) {
            dispatch(addActivityToJourney({
                activity: updatedActivity,
                travelComponentActivities: activities,
                day: dayToAdd.current
            }))
            _renderToast(
                t("TOASTS.SUCCESS.ACTIVITY_ADDED_TO_JOURNEY", {
                    activity: generateTranslatedActivityTypeString(updatedActivity.common.activityType, i18n),
                    interpolation: {escapeValue: false}
                }),
                "success"
            )
        }
    }

    const _addActivityToJourney = (activity: AllActivityTypes) => {
        const updatedActivity = cloneDeep(activity)
        if (isActivityWithReturnRouteProps(updatedActivity)) {
            const {returnActivity, oneWayActivity} = getReturnAndOneWayActivity(updatedActivity, activities)
            returnActivity && _addActivity(returnActivity)
            oneWayActivity && _addActivity(oneWayActivity)
        } else {
            _addActivity(updatedActivity)
        }
        dayToAdd.current = null
    }

    const _onSelectAddActivityToJourney = (index: number, selection: DropdownOption[] | null) => {
        if (selection) {
            dayToAdd.current = selection[0].value
            const activityToMoveToJourney = cloneDeep(activities[index])
            if (journeyHasExactDates) {
                const {common: {activityType}} = activityToMoveToJourney
                if (recalculateActivityOnDifferentDate(activityType)) {
                    recalculateActivityEmissionWitNewGivenDay(activityToMoveToJourney, dayToAdd.current)
                } else {
                    _addActivityToJourney(activityToMoveToJourney)
                }
            } else {
                _addActivityToJourney(activityToMoveToJourney)
            }
        }
    }

    const _setDataGridRows = () => {
        const disableAddTravelComponentToJourney = journeyDurationInDays === null || journeyDurationInDays === 0
        const noDisplayOfReturnFlights = activities
            .map(activity => {
                if (isActivityFlightState(activity) && activity.returnId) return activity.returnId
            })
            .filter(isDefined)

        return activities.map((activity, index): DataGridDataRowProps["rows"][0] => {
            const {common: {activityType, id}} = activity
            const {status, emissionKgCO2eTotal: co2ETotal} = getTotalCo2KgEEmissionsAndCalculationStatus(activity)
            const customCell = <ActivityEmissionDisplay
                totalCo2KgEIncludingReturnEmissionValues={true}
                allActivities={activities}
                activity={activity}
                key={index}/>
            const lastCell = _setLastTableCellFromRow(status, customCell, co2ETotal, activity)
            let actions = _addTravelComponentsToJourney(disableAddTravelComponentToJourney, index, activity)
            if (actions) {
                actions.unshift(..._editDeleteActions())
            } else {
                actions = _editDeleteActions()
            }
            switch (activityType) {
                case ACTIVITY_TYPE.flight:
                    if (isActivityFlightState(activity)) {
                        const {
                            legs,
                            passengerCount,
                            common: {id}
                        } = activity
                        if (!noDisplayOfReturnFlights.includes(id)) {
                            const firstLeg = getFirstLeg(legs)
                            const lastLeg = getLastLeg(legs)
                            const departureAirportIata = firstLeg?.airport?.iata
                            const arrivalAirportIata = lastLeg?.airport?.iata
                            let dateMilliseconds: number | null = null
                            let date: DateTime | null = null
                            if (firstLeg) {
                                date = firstLeg.date
                                dateMilliseconds = date?.toMillis() ?? null
                            }
                            return {
                                data: [
                                    {
                                        customCell: <FlightRoute
                                            iconColor={theme.colors.black}
                                            flight={activity}
                                            showReturnIcon={true}
                                            key={index}/>,
                                        value: `${departureAirportIata}${arrivalAirportIata}`,
                                        keyPathToValue: KEYPATH_TO_VALUE.iata
                                    },
                                    {
                                        text: passengerCount,
                                        value: passengerCount,
                                        keyPathToValue: KEYPATH_TO_VALUE.passengerCount
                                    },
                                    {
                                        text: date ? formatDateString(date, resolvedLanguage, false) : NO_DATA_STRING,
                                        value: dateMilliseconds ?? -1,
                                    },
                                    lastCell,
                                ],
                                group: generateTranslatedActivityTypeString(activity.common.activityType, i18n),
                                key: id,
                                rawData: activity,
                                actions
                            }
                        }
                    }
                    break;
                case ACTIVITY_TYPE.hotel:
                    if (isActivityHotelState(activity)) {
                        const {
                            hotel,
                            city,
                            country,
                            guestCount,
                            hotelStayDuration,
                        } = activity
                        const hasExactHotelDates = hasExactDates(hotelStayDuration)
                        let formattedStartDate = ''
                        let formattedEndDate = ''
                        let sortingStartDateMilliseconds = -1
                        if (hasExactHotelDates) {
                            const {startDate, endDate} = hotelStayDuration.dates!!
                            formattedStartDate = startDate ? formatDateString(startDate, resolvedLanguage, false) : NO_DATA_STRING
                            formattedEndDate = endDate ? formatDateString(endDate, resolvedLanguage, false) : NO_DATA_STRING
                            sortingStartDateMilliseconds = startDate.toMillis()
                        }

                        return {
                            data: [
                                {
                                    customCell: <HotelLocation
                                        iconColor={theme.colors.black}
                                        whereToDisplay={WhereToDisplayActivityString.travelComponents}
                                        hotel={activity}
                                        key={index}/>,
                                    value: `${hotel ? `${hotel.name}` : ''}${city?.name}${country?.alpha3}`,
                                    keyPathToValue: KEYPATH_TO_VALUE.city
                                },
                                {
                                    text: guestCount,
                                    value: guestCount
                                },
                                {
                                    text: hasExactHotelDates ? `${formattedStartDate}-${formattedEndDate}` : NO_DATA_STRING,
                                    value: sortingStartDateMilliseconds
                                },
                                lastCell,
                            ],
                            group: generateTranslatedActivityTypeString(activity.common.activityType, i18n),
                            key: id,
                            rawData: activity,
                            actions
                        }
                    }
                    break;
                case ACTIVITY_TYPE.car_bus:
                    if (isActivityCarBusState(activity)) {
                        const {passengerCount, fuelType} = activity
                        return {
                            data: [
                                {
                                    customCell: <CarBusInformation
                                        iconColor={theme.colors.black}
                                        whereToDisplay={WhereToDisplayActivityString.travelComponents}
                                        carBus={activity}
                                        key={index}/>,
                                    value: `${fuelType}`,
                                    keyPathToValue: KEYPATH_TO_VALUE.fuelType,
                                },
                                {
                                    text: passengerCount,
                                    value: passengerCount
                                },
                                {
                                    text: NO_DATA_STRING,
                                    value: -1
                                },
                                lastCell,
                            ],
                            group: generateTranslatedActivityTypeString(activity.common.activityType, i18n),
                            key: id,
                            rawData: activity,
                            actions
                        }
                    }
                    break;
                default:
                    return activityType satisfies never
            }
        }).filter(isDefined)
    }

    const _isActivitySelected = (activity: AllActivityTypes) => {
        if (selectedRows.length > 0) return selectedRows.some(selectedRowIndex => isEqual(rows[selectedRowIndex]?.rawData, activity))
        return true
    }

    const _sortActivities = () => {
        const sortProps = getSortProps(headers);
        let activitiesSorted = cloneDeep(activities) as AllActivityTypesArray;

        if (sortProps) {
            const {indexesOfColumnsToSort, sortOrders} = sortProps;
            const rowForDataGrid = _setDataGridRows()[0];

            if (rowForDataGrid && rowForDataGrid.data) {
                const sortByFunctions = indexesOfColumnsToSort.map((indexOfColumnToSort) => {
                    const {keyPathToValue} = rowForDataGrid.data![indexOfColumnToSort];
                    if (keyPathToValue) {

                        if (keyPathToValue === KEYPATH_TO_VALUE.passengerCount) return (item: AllActivityTypes) =>
                            get(item, KEYPATH_TO_VALUE.passengerCount)!
                        if (keyPathToValue === KEYPATH_TO_VALUE.calculatedEmissions) return (item: AllActivityTypes) => {
                            const personsCount = getActivityPersonCount(item)
                            if (personsCount !== null) return get(item, KEYPATH_TO_VALUE.calculatedEmissions)! / personsCount
                        }
                        if (keyPathToValue === KEYPATH_TO_VALUE.iata) return (item: AllActivityTypes) => {
                            const personsCount = getActivityPersonCount(item)
                            if (typeof personsCount === 'number') {
                                return [
                                    get(item, 'legs.firstLeg.airport.iata')!,
                                    get(item, 'legs.lastLeg.iata')!,
                                    get(item, KEYPATH_TO_VALUE.calculatedEmissions)! /
                                    personsCount,
                                ]
                            }
                        }
                        if (keyPathToValue === KEYPATH_TO_VALUE.city) return (item: AllActivityTypes) => {
                            const personsCount = getActivityPersonCount(item)
                            if (typeof personsCount === 'number') return [
                                get(item, 'city.name')!,
                                get(item, KEYPATH_TO_VALUE.calculatedEmissions)! /
                                personsCount,
                            ]
                        }
                    }

                    return (item: AllActivityTypes) => item
                });

                activitiesSorted = orderBy(
                    activitiesSorted,
                    sortByFunctions,
                    indexesOfColumnsToSort.map(index => sortOrders[index] === SORT_ORDER.DESC ? 'desc' : 'asc')
                );
            }
        }

        return activitiesSorted;
    }

    const _selectRow = (index: number) => {
        if (selectedRows.includes(index)) {
            setSelectedRows(selectedRows.filter(row => row !== index));
        } else {
            setSelectedRows([...selectedRows, index])
        }
    }

    const _editDeleteActions = (): DataGridRowActionInterface[] => {
        return [
            {
                icon: "penToSquare",
                onClick: (_, activity) => {
                    const {common: {id, activityType}} = activity
                    navigate(`${PATH_STRINGS.editActivity}/${PATH_SCREEN_STRING.tc}/${activityType}/${id}/`)
                },
                tooltip: "TOOLTIP.COMMON.EDIT_TRAVEL_COMPONENT",
            },
            {
                icon: "trash",
                onClick: _deleteActivity,
                tooltip: "TOOLTIP.COMMON.DELETE_TRAVEL_COMPONENT",
            }
        ]
    }

    return <>
        <TopButtonsWrapper
            leftButtons={
                <>
                    <IconAndText
                        disabled={activities.length === 0}
                        containerStyle={{marginTop: "auto"}}
                        size={"medium"}
                        iconColor={theme.colors.primary}
                        text={selectedRows.length === 0 ? "GENERAL.SELECT_ALL" : "GENERAL.UNSELECT_ALL"}
                        onClick={() => selectedRows.length === 0 ? setSelectedRows(activities.map((_, index) => index)) : setSelectedRows([])}
                        icon={'squareCheck'}/>
                    <IconAndText
                        disabled={activities.length === 0}
                        containerStyle={{marginTop: "auto"}}
                        text={groupRows ? "GENERAL.UNGROUP_TRAVEL_COMPONENTS" : "GENERAL.GROUP_TRAVEL_COMPONENTS"}
                        iconColor={theme.colors.primary}
                        size={"medium"}
                        onClick={() => setGroupRows(!groupRows)}
                        icon={'tableList'}/>
                </>
            }
            rightButtons={
                <>
                    <DownloadPdfButton
                        whatPdfToCreate={BundleType.travelComponents}
                        activities={_sortActivities().filter(_isActivitySelected)}
                    />
                    <CopyToClipboardButton
                        whatToCopy={BundleType.travelComponents}
                        activities={_sortActivities().filter(_isActivitySelected)}/>
                    <LoadBundlesButton
                        showBundleIcon={false}
                        whatToLoad={BundleType.travelComponents}/>
                    <SaveBundlesButton
                        bundleHasBeenLoaded={bundleHasBeenLoaded}
                        whatToSave={BundleType.travelComponents}
                        activities={activities.filter(_isActivitySelected)}
                    />
                    <CreateJourneyFromTravelComponentsButton
                        activities={_sortActivities().filter(_isActivitySelected)}/>
                    <DeleteBundlesButton
                        onDelete={() => setSelectedRows([])}
                        activities={selectedRows.length === 0 ? [] : activities.filter(_isActivitySelected)}
                        whatToDelete={BundleType.travelComponents}/>
                </>
            }
        />
        {activities.length > 0 ?
            <>
                {headers.length > 0 && rows.length > 0 && <DataGrid
                    checkboxLabel={"LABELS.TRAVEL_COMPONENTS.SELECT_TRAVEL_COMPONENT"}
                    look={"primaryLight"}
                    groupRows={groupRows}
                    cellWeights={[3, 1, 1, 2]}
                    headers={headers}
                    rows={rows}
                    rowSize={"medium"}
                    selectedRows={selectedRows}
                    onSelectRow={index => _selectRow(index)}/>}
            </> :
            <div className={"travel-components-table-no-data-wrapper"}>
                <Text variant={"headlineMedium"}>
                    SCREENS.TRAVEL_COMPONENTS_OVERVIEW.NO_TRAVEL_COMPONENTS
                </Text>
                <TextButton
                    onClick={() => navigate(PATH_STRINGS.selectTravelComponents)}>
                    SCREENS.TRAVEL_COMPONENTS_OVERVIEW.ADD_TRAVEL_COMPONENTS
                </TextButton>
            </div>}
    </>
}

export default TravelComponentsTable